import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green, red, orange, blue } from "@material-ui/core/colors";

// Icons
import BusinessIcon from "@material-ui/icons/Business";
import InfoIcon from "@material-ui/icons/Info";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SettingsIcon from "@material-ui/icons/Settings";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import LinkIcon from "@material-ui/icons/Link";
import ScheduleIcon from "@material-ui/icons/Schedule";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

// Função para gerar URL correta da imagem
const getImageUrl = (mediaPath) => {
  if (!mediaPath) return null;
  
  console.log('🖼️ [DEBUG MODAL] mediaPath recebido:', mediaPath);
  
  // Aplica a mesma correção que funciona no MediaComponents
  const correctedUrl = mediaPath.replace('localhost:443/', 'localhost:8080/');
  
  console.log('🖼️ [DEBUG MODAL] URL corrigida:', correctedUrl);
  return correctedUrl;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  
  dialogPaper: {
    maxWidth: 800,
    width: "95vw",
    backgroundColor: theme.palette.background.paper,
  },

  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    marginBottom: 0, // Remove margem inferior
    "& .MuiTypography-h6": {
      fontWeight: 600,
      fontSize: "1.2rem",
    }
  },

  dialogContent: {
    padding: 0,
    paddingTop: 0, // Remove padding superior
    overflowY: "auto",
    maxHeight: "70vh", // Limita altura para forçar scroll
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.action.hover,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      }
    }
  },

  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    // Remove position sticky pois agora está fora do DialogContent
  },

  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    minHeight: 64,
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    }
  },

  tabPanel: {
    padding: theme.spacing(3),
    minHeight: 400,
    backgroundColor: theme.palette.background.paper,
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },

  sectionIcon: {
    color: theme.palette.primary.main,
  },

  tooltipIcon: {
    fontSize: "1rem",
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(0.5),
    cursor: "help",
    "&:hover": {
      color: theme.palette.primary.dark,
    }
  },

  priorityChip: {
    fontWeight: 600,
    "& .MuiChip-icon": {
      fontSize: "1.2rem",
    }
  },

  priorityHigh: {
    backgroundColor: red[50],
    color: red[600],
    border: `1px solid ${red[200]}`,
  },

  priorityMedium: {
    backgroundColor: orange[50], 
    color: orange[600],
    border: `1px solid ${orange[200]}`,
  },

  priorityLow: {
    backgroundColor: green[50],
    color: green[600],
    border: `1px solid ${green[200]}`,
  },

  switchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },

  switchLabel: {
    margin: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.95rem",
      fontWeight: 500,
    }
  },

  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      }
    }
  },

  selectField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
    }
  },

  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1,
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    borderRadius: 4,
    padding: theme.spacing(1, 3),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    }
  },

  cancelButton: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    }
  },

  // Estilos para a nova seção de anexos
  attachmentSection: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    minHeight: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    }
  },

  attachmentWithImage: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    }
  },

  attachmentInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },

  attachmentImagePreview: {
    width: 200,
    height: 140,
    objectFit: "cover",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },

  attachmentFileName: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },

  attachmentActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  
  colorAdorment: {
    width: 20,
    height: 20,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`announcement-tabpanel-${index}`}
      aria-labelledby={`announcement-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `announcement-tab-${index}`,
    'aria-controls': `announcement-tabpanel-${index}`,
  };
}

const AnnouncementModal = ({ open, onClose, announcementId, reload }) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Schema dinâmico baseado nas permissões do usuário
  const getAnnouncementSchema = () => {
    const allowedAudiences = user.super 
      ? ["all", "admins", "users", "super"] 
      : ["all", "admins", "users"];
    
    return Yup.object().shape({
      title: Yup.string().required("Obrigatório").max(80, "Título deve ter no máximo 80 caracteres"),
      text: Yup.string().required("Obrigatório").max(1000, "Texto deve ter no máximo 1000 caracteres"),
      priority: Yup.number().min(1).max(3).required("Obrigatório"),
      targetAudience: Yup.string().oneOf(allowedAudiences),
      maxDisplayCount: Yup.number().min(1).max(999),
      displayInterval: Yup.number().min(0),
      persistenceTimer: Yup.number().min(1).max(300),
      category: Yup.string().max(50),
      linkText: Yup.string().max(40, "Texto do botão deve ter no máximo 40 caracteres"),
      externalUrl: Yup.string().url("URL inválida")
    });
  };

  const initialState = {
    // Campos básicos
    title: "",
    text: "",
    priority: 2,
    status: true,
    
    // Multi-tenancy (OBRIGATÓRIO conforme documentação)
    targetCompanyId: null, // null = global, number = empresa específica
    
    // Sistema de modal
    showAsModal: false,
    requiresAcknowledge: false,
    requiresPersistence: false, // Controlado automaticamente por requiresAcknowledge
    persistenceTimer: 10, // Sempre 10 segundos quando requiresAcknowledge = true
    
    // Sistema de repetições
    maxDisplayCount: 1,
    displayInterval: 24,
    allowDismissForever: true,
    blockDismissForever: false, // Sempre false - controlado apenas por allowDismissForever
    
    // Público-alvo
    targetAudience: "all",
    
    // Sistema de expiração
    expiresAt: null,
    neverExpires: true,
    
    // Agendamento
    publishAt: null,
    isScheduled: false,
    
    // Links externos
    externalUrl: "",
    linkText: "",
    openInNewTab: true,
    
    // Categoria
    category: "general"
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  // Carregar lista de empresas quando o modal abrir
  useEffect(() => {
    if (open && (user.profile === "super" || (user.profile === "admin" && user.companyId === 1))) {
      loadCompanies();
    }
  }, [open, user]);

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const { data } = await api.get("/companies/list");
      console.log("🏢 [DEBUG] Empresas carregadas:", data);
      setCompanies(data);
    } catch (error) {
      console.error("❌ [ERROR] Erro ao carregar empresas:", error);
      toastError(error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        if (!announcementId) {
          // Para Admin de outras empresas (não empresa 1), definir automaticamente targetCompanyId
          if (user.profile === "admin" && user.companyId !== 1) {
            setAnnouncement(prev => ({
              ...prev,
              targetCompanyId: user.companyId
            }));
          }
          return;
        }

        const { data } = await api.get(`/announcements/${announcementId}`);
        setAnnouncement((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [announcementId, open, user]);

  const handleClose = () => {
    setAnnouncement(initialState);
    setAttachment(null);
    setTabValue(0);
    setCompanies([]); // Limpar lista de empresas
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveAnnouncement = async (values) => {
    console.log("💾 Salvando anúncio com valores:", values);
    
    const announcementData = { ...values };
    
    // Converter strings vazias para null em campos integer
    if (announcementData.targetCompanyId === "") {
      announcementData.targetCompanyId = null;
    }
    
    // Converter strings vazias para null em campos de data
    if (announcementData.expiresAt === "" || announcementData.expiresAt === "Invalid date") {
      announcementData.expiresAt = null;
    }
    
    if (announcementData.publishAt === "" || announcementData.publishAt === "Invalid date") {
      announcementData.publishAt = null;
    }
    
    console.log("📋 Dados processados para envio:", announcementData);
    
    try {
      if (announcementId) {
        console.log("✏️ Editando anúncio ID:", announcementId);
        await api.put(`/announcements/${announcementId}`, announcementData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);
          await api.post(
            `/announcements/${announcementId}/media-upload`,
            formData
          );
        }
      } else {
        console.log("➕ Criando novo anúncio");
        const { data } = await api.post("/announcements", announcementData);
        console.log("✅ Anúncio criado com ID:", data.id);
        if (attachment != null) {
          console.log("📎 Enviando anexo para anúncio ID:", data.id);
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);

          await api.post(`/announcements/${data.id}/media-upload`, formData);
          console.log("✅ Anexo enviado com sucesso");
        }
      }
      console.log("🎉 Anúncio salvo com sucesso!");
      toast.success(i18n.t("announcements.toasts.success"));
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      console.error("❌ Erro ao salvar anúncio:", err);
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (announcement.mediaPath) {
      await api.delete(`/announcements/${announcement.id}/media-upload`);
      setAnnouncement((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("announcements.toasts.deleted"));
      if (typeof reload == "function") {
        reload();
      }
    }
    setConfirmationOpen(false);
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("announcements.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon />
            {announcementId
              ? `${i18n.t("announcements.dialog.edit")}`
              : `${i18n.t("announcements.dialog.add")}`}
          </Box>
        </DialogTitle>
        
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        
        <Formik
          initialValues={announcement}
          enableReinitialize={true}
          validationSchema={getAnnouncementSchema()}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveAnnouncement(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <Tabs
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                className={classes.tabsContainer}
                variant="fullWidth"
                TabIndicatorProps={{ style: { backgroundColor: '#667eea' } }}
              >
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoIcon />
                    Informações & Anexos
                  </Box>
                }
                className={classes.tab}
                {...a11yProps(0)}
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <SettingsIcon />
                    Configurações
                  </Box>
                }
                className={classes.tab}
                {...a11yProps(1)}
              />
            </Tabs>
            
            <DialogContent className={classes.dialogContent}>

              <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
                {/* ABA 1: INFORMAÇÕES BÁSICAS & ANEXOS */}
                <Grid spacing={3} container>
                  {/* Seção Informações Básicas */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <Typography variant="h6">
                          Informações Básicas
                        </Typography>
                      </Box>
                      
                      <Grid spacing={2} container>
                          <Grid xs={12} item>
                            <Box display="flex" alignItems="center">
                              <Field
                                as={TextField}
                                label={i18n.t("announcements.dialog.form.title")}
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                className={classes.textField}
                                inputProps={{ maxLength: 80 }}
                              />
                            </Box>
                          </Grid>
                          
                          <Grid xs={12} item>
                            <Box display="flex" alignItems="flex-start">
                              <Field
                                as={TextField}
                                label={i18n.t("announcements.dialog.form.text")}
                                name="text"
                                error={touched.text && Boolean(errors.text)}
                                helperText={touched.text && errors.text}
                                variant="outlined"
                                margin="dense"
                                multiline
                                minRows={4}
                                maxRows={8}
                                fullWidth
                                className={classes.textField}
                                inputProps={{ maxLength: 1000 }}
                              />
                            </Box>
                          </Grid>

                  {/* Seção Anexo */}
                  <Grid xs={12} item>
                    <Box>
                      {/* Layout condicional baseado na presença de imagem */}
                      {(attachment || announcement.mediaPath) ? (
                        // Layout com imagem: info à esquerda, imagem à direita
                        <Box className={classes.attachmentWithImage}>
                          <Box className={classes.attachmentInfo}>
                            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600, marginBottom: 4 }}>
                              Anexo
                            </Typography>
                            <Typography variant="body2" className={classes.attachmentFileName}>
                              {attachment ? attachment.name : announcement.mediaName || "Imagem anexada"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {attachment ? "Nova imagem selecionada" : "Imagem atual do anúncio"}
                            </Typography>
                            <Box className={classes.attachmentActions}>
                              <Button 
                                size="small" 
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                onClick={() => attachmentFile.current.click()}
                              >
                                Alterar
                              </Button>
                              <IconButton
                                onClick={() => setConfirmationOpen(true)}
                                color="secondary"
                                size="small"
                                title="Remover imagem"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          {/* Preview da imagem */}
                          <Box>
                            {attachment ? (
                              <img
                                src={URL.createObjectURL(attachment)}
                                alt="Preview da nova imagem"
                                className={classes.attachmentImagePreview}
                              />
                            ) : announcement.mediaPath ? (
                              <img
                                src={getImageUrl(announcement.mediaPath)}
                                alt="Imagem atual"
                                className={classes.attachmentImagePreview}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      ) : (
                        // Layout sem imagem: quadro clicável
                        <Box 
                          className={classes.attachmentSection}
                          onClick={() => attachmentFile.current.click()}
                        >
                          <Box textAlign="center">
                            <AttachFileIcon fontSize="large" color="primary" />
                            <Typography variant="body1" color="textSecondary" style={{ marginTop: 8 }}>
                              Clique para selecionar uma imagem
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Formatos aceitos: PNG, JPG, JPEG
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                          <Grid xs={6} item>
                            <Box display="flex" alignItems="center">
                              <FormControl variant="outlined" margin="dense" fullWidth className={classes.selectField}>
                                <InputLabel>Prioridade</InputLabel>
                                <Field
                                  as={Select}
                                  label="Prioridade"
                                  name="priority"
                                  MenuProps={{
                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                    transformOrigin: { vertical: "top", horizontal: "left" },
                                    getContentAnchorEl: null,
                                  }}
                                >
                                <MenuItem value={1}>
                                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                                    <Chip
                                      label="Alta"
                                      size="small"
                                      className={`${classes.priorityChip} ${classes.priorityHigh}`}
                                      icon={<PriorityHighIcon />}
                                    />
                                  </Box>
                                </MenuItem>
                                <MenuItem value={2}>
                                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                                    <Chip
                                      label="Média"
                                      size="small"
                                      className={`${classes.priorityChip} ${classes.priorityMedium}`}
                                      icon={<InfoIcon />}
                                    />
                                  </Box>
                                </MenuItem>
                                <MenuItem value={3}>
                                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                                    <Chip
                                      label="Baixa"
                                      size="small"
                                      className={`${classes.priorityChip} ${classes.priorityLow}`}
                                      icon={<CheckCircleIcon />}
                                    />
                                  </Box>
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Box>
                          </Grid>

                          <Grid xs={6} item>
                            <Box display="flex" alignItems="center">
                              <FormControl variant="outlined" margin="dense" fullWidth className={classes.selectField}>
                                <InputLabel>Categoria</InputLabel>
                                <Field
                                  as={Select}
                                  label="Categoria"
                                  name="category"
                                  MenuProps={{
                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                    transformOrigin: { vertical: "top", horizontal: "left" },
                                    getContentAnchorEl: null,
                                  }}
                                >
                                <MenuItem value="general">📋 Geral</MenuItem>
                                <MenuItem value="maintenance">🔧 Manutenção</MenuItem>
                                <MenuItem value="update">🆙 Atualização</MenuItem>
                                <MenuItem value="news">📰 Novidades</MenuItem>
                                <MenuItem value="alert">⚠️ Alerta</MenuItem>
                                <MenuItem value="promotion">🎉 Promoção</MenuItem>
                              </Field>
                            </FormControl>
                          </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>



                  {/* Seção Links Externos */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <LinkIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Link Externo
                        </Typography>
                      </Box>
                        
                        <Grid spacing={2} container>
                          <Grid xs={12} item>
                            <Box display="flex" alignItems="center">
                              <Field
                                as={TextField}
                                label="URL/LINK Externo (com https://)"
                                name="externalUrl"
                                error={touched.externalUrl && Boolean(errors.externalUrl)}
                                helperText={touched.externalUrl && errors.externalUrl}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                className={classes.textField}
                                placeholder="https://exemplo.com"
                              />
                            </Box>
                          </Grid>
                          
                          <Grid xs={8} item>
                            <Box display="flex" alignItems="center">
                              <Field
                                as={TextField}
                                label="Texto do botão"
                                name="linkText"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                className={classes.textField}
                                placeholder="Clique aqui para saber mais"
                                inputProps={{ maxLength: 40 }}
                              />

                            </Box>
                          </Grid>

                          <Grid xs={4} item>
                            <Box className={classes.switchContainer} display="flex" alignItems="center">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.openInNewTab}
                                    onChange={(event) => setFieldValue("openInNewTab", event.target.checked)}
                                    color="primary"
                                  />
                                }
                                label="Abrir em nova aba"
                                className={classes.switchLabel}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
                {/* ABA 2: CONFIGURAÇÕES */}
                <Grid spacing={3} container>
                  {/* Seção Público-Alvo e Empresa */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <BusinessIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Público-Alvo
                        </Typography>
                      </Box>
                      
                      <Grid spacing={2} container>
                          <Grid xs={6} item>
                            <Box display="flex" alignItems="center">
                              <FormControl variant="outlined" margin="dense" fullWidth className={classes.selectField}>
                                <InputLabel>Público-Alvo</InputLabel>
                                <Select
                                  label="Público-Alvo"
                                  name="targetAudience"
                                  value={values.targetAudience || "all"}
                                  onChange={(event) => setFieldValue("targetAudience", event.target.value)}
                                  MenuProps={{
                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                    transformOrigin: { vertical: "top", horizontal: "left" },
                                    getContentAnchorEl: null,
                                  }}
                                >
                                  <MenuItem value="all">👥 Todos os usuários</MenuItem>
                                  <MenuItem value="admins">👨‍💼 Apenas administradores</MenuItem>
                                  <MenuItem value="users">👤 Apenas usuários</MenuItem>
                                  {/* Super usuários só aparece para super admin */}
                                  {user.super && (
                                    <MenuItem value="super">⭐ Super usuários</MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </Box>
                          </Grid>

                          {/* Campo Empresa Alvo - só aparece para Super Admin e Admin Empresa 1 */}
                          {(user.super || (user.profile === "admin" && user.companyId === 1)) && (
                            <Grid xs={6} item>
                              <FormControl variant="outlined" margin="dense" fullWidth className={classes.selectField}>
                                <InputLabel>Empresa Alvo</InputLabel>
                                <Select
                                  label="Empresa Alvo"
                                  name="targetCompanyId"
                                  value={values.targetCompanyId || ""}
                                  onChange={(event) => setFieldValue("targetCompanyId", event.target.value === "" ? null : event.target.value)}
                                  disabled={loadingCompanies}
                                  MenuProps={{
                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                    transformOrigin: { vertical: "top", horizontal: "left" },
                                    getContentAnchorEl: null,
                                  }}
                                >
                                  {loadingCompanies ? (
                                    <MenuItem disabled>
                                      <CircularProgress size={20} /> Carregando empresas...
                                    </MenuItem>
                                  ) : (
                                    [
                                      /* SUPER ADMIN: Opção global + todas as empresas */
                                      ...(user.super ? [
                                        <MenuItem key="global" value="">🌐 Todas as empresas (Global)</MenuItem>,
                                        ...companies.map(company => (
                                          <MenuItem key={`super-${company.id}`} value={company.id}>🏢 {company.name}</MenuItem>
                                        ))
                                      ] : []),
                                      
                                      /* ADMIN EMPRESA 1: Apenas empresas específicas (somente se não for super admin) */
                                      ...(!user.super && user.profile === "admin" && user.companyId === 1 ? 
                                        companies.map(company => (
                                          <MenuItem key={`admin1-${company.id}`} value={company.id}>🏢 {company.name}</MenuItem>
                                        )) : []
                                      )
                                    ]
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                  {/* Seção Agendamento */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <ScheduleIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Agendamento e Expiração
                        </Typography>
                      </Box>
                      
                      <Grid spacing={2} container>
                          <Grid xs={6} item>
                            <Box className={`${classes.switchContainer} ${values.isScheduled ? 'active' : ''}`} display="flex" alignItems="center" justifyContent="space-between">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.isScheduled}
                                    onChange={(event) => {
                                      console.log("📅 Toggle agendar publicação:", event.target.checked);
                                      setFieldValue("isScheduled", event.target.checked);
                                      if (!event.target.checked) {
                                        setFieldValue("publishAt", null);
                                      }
                                    }}
                                    color="primary"
                                  />
                                }
                                label="Agendar publicação"
                                className={classes.switchLabel}
                              />
                            </Box>
                            
                            <Field
                              as={TextField}
                              label={!values.isScheduled ? "Data/Hora de Publicação (Desabilitado)" : "Data/Hora de Publicação"}
                              name="publishAt"
                              type="datetime-local"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textField}
                              InputLabelProps={{ shrink: true }}
                              disabled={!values.isScheduled}
                              style={{
                                opacity: !values.isScheduled ? 0.6 : 1
                              }}
                              helperText={!values.isScheduled ? "Ative 'Agendar publicação' para usar este campo" : ""}
                            />
                          </Grid>

                          <Grid xs={6} item>
                            <Box className={`${classes.switchContainer} ${values.neverExpires ? 'active' : ''}`} display="flex" alignItems="center" justifyContent="space-between">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.neverExpires}
                                    onChange={(event) => {
                                      console.log("⏰ Toggle nunca expira:", event.target.checked);
                                      setFieldValue("neverExpires", event.target.checked);
                                      if (event.target.checked) {
                                        setFieldValue("expiresAt", null);
                                      }
                                    }}
                                    color="primary"
                                  />
                                }
                                label="Nunca expira"
                                className={classes.switchLabel}
                              />
                            </Box>
                            
                            <Field
                              as={TextField}
                              label={values.neverExpires ? "Data/Hora de Expiração (Desabilitado)" : "Data/Hora de Expiração"}
                              name="expiresAt"
                              type="datetime-local"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textField}
                              InputLabelProps={{ shrink: true }}
                              disabled={values.neverExpires}
                              style={{
                                opacity: values.neverExpires ? 0.6 : 1
                              }}
                              helperText={values.neverExpires ? "Desative 'Nunca expira' para usar este campo" : ""}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                  {/* Seção Comportamento do Modal */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <SettingsIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Comportamento do Modal
                        </Typography>
                      </Box>
                        
                        <Grid spacing={2} container>
                          <Grid xs={6} item>
                            <Box className={`${classes.switchContainer} ${values.showAsModal ? 'active' : ''}`} display="flex" alignItems="center" justifyContent="space-between">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.showAsModal}
                                    onChange={(event) => {
                                      console.log("🪟 Toggle exibir como modal:", event.target.checked);
                                      setFieldValue("showAsModal", event.target.checked);
                                    }}
                                    color="primary"
                                  />
                                }
                                label="Exibir como modal"
                                className={classes.switchLabel}
                              />
                              <Tooltip title="Se ativado, o anúncio aparecerá como uma janela popup na tela do usuário" arrow>
                                <InfoIcon className={classes.tooltipIcon} />
                              </Tooltip>
                            </Box>
                          </Grid>

                          <Grid xs={6} item>
                            <Box className={classes.switchContainer}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.requiresAcknowledge}
                                    disabled={!values.showAsModal}
                                    onChange={(event) => {
                                      const isChecked = event.target.checked;
                                      console.log("✅ Toggle requer confirmação:", isChecked);
                                      setFieldValue("requiresAcknowledge", isChecked);
                                      
                                      // Quando ativar confirmação, ajustar automaticamente
                                      if (isChecked) {
                                        setFieldValue("requiresPersistence", true);
                                        setFieldValue("persistenceTimer", 10);
                                        // Desabilitar controles de exibição quando confirmação estiver ativa
                                        setFieldValue("allowDismissForever", false);
                                      } else {
                                        setFieldValue("requiresPersistence", false);
                                        setFieldValue("persistenceTimer", 10);
                                        // Reabilitar controles quando confirmação for desativada
                                        setFieldValue("allowDismissForever", true);
                                      }
                                    }}
                                    color="primary"
                                  />
                                }
                                label={!values.showAsModal ? "Requer confirmação de leitura (Requer Modal)" : "Requer confirmação de leitura"}
                                className={classes.switchLabel}
                                style={{
                                  opacity: !values.showAsModal ? 0.5 : 1
                                }}
                              />
                              <Tooltip title={!values.showAsModal ? "Esta função só funciona quando 'Exibir como modal' estiver ativado" : "MODO AVANÇADO: O usuário controla quando parar de ver o anúncio. Aparece 'Li e entendi' (para de exibir) e 'Ver depois' (continua aparecendo). Desabilita outros controles de exibição automaticamente."} arrow>
                                <InfoIcon className={classes.tooltipIcon} />
                              </Tooltip>
                            </Box>
                          </Grid>
                          
                          {values.requiresAcknowledge && (
                            <Grid xs={12} item>
                              <Box 
                                style={{ 
                                  marginTop: 8, 
                                  padding: 12, 
                                  backgroundColor: '#e3f2fd', 
                                  borderRadius: 4,
                                  border: '1px solid #2196f3'
                                }}
                              >
                                <Typography variant="body2" style={{ color: '#1976d2', fontWeight: 500 }}>
                                  📋 <strong>Modo Confirmação Ativo:</strong>
                                </Typography>
                                <Typography variant="caption" style={{ color: '#1565c0', display: 'block', marginTop: 4 }}>
                                  • Modal fica 10s na tela, depois mostra: "Li e entendi" + "Ver depois"<br/>
                                  • <strong>"Li e entendi":</strong> Marca como lida e para de aparecer<br/>
                                  • <strong>"Ver depois":</strong> NÃO marca como lida, aparece novamente<br/>
                                  • Controles de exibição automáticos ficam desabilitados
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                  {/* Seção Controle de Exibição */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <VisibilityIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Controle de Exibição
                          {values.requiresAcknowledge && (
                            <Chip 
                              label="DESABILITADO - Modo Confirmação Ativo" 
                              size="small" 
                              style={{ 
                                marginLeft: 12, 
                                backgroundColor: '#ffecb3', 
                                color: '#e65100',
                                fontWeight: 600
                              }} 
                            />
                          )}
                        </Typography>
                      </Box>
                        
                        <Grid spacing={2} container>
                          <Grid xs={6} item>
                            <Field
                              as={TextField}
                              label="Máximo de Exibições"
                              name="maxDisplayCount"
                              type="number"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textField}
                              inputProps={{ min: 1, max: 999 }}
                              disabled={values.requiresAcknowledge}
                              helperText={values.requiresAcknowledge ? "Controlado pelo usuário no modo confirmação" : ""}
                            />
                          </Grid>

                          <Grid xs={6} item>
                            <Field
                              as={TextField}
                              label="Intervalo entre Exibições (horas)"
                              name="displayInterval"
                              type="number"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textField}
                              inputProps={{ min: 0 }}
                              disabled={values.requiresAcknowledge}
                              helperText={values.requiresAcknowledge ? "Controlado pelo usuário no modo confirmação" : ""}
                            />
                          </Grid>

                          <Grid xs={12} item>
                            <Box className={classes.switchContainer} style={{
                              opacity: values.requiresAcknowledge ? 0.5 : 1,
                              pointerEvents: values.requiresAcknowledge ? 'none' : 'auto'
                            }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.allowDismissForever}
                                    onChange={(event) => setFieldValue("allowDismissForever", event.target.checked)}
                                    color="primary"
                                    disabled={values.requiresAcknowledge}
                                  />
                                }
                                label={values.requiresAcknowledge ? "Permitir dispensar para sempre (DESABILITADO)" : "Permitir dispensar para sempre"}
                                className={classes.switchLabel}
                              />
                              <Tooltip title={
                                values.requiresAcknowledge 
                                  ? "No modo confirmação, o usuário controla quando parar de ver através dos botões 'Li e entendi' ou 'Ver depois'"
                                  : "Se ativado, usuário verá o botão 'Não mostrar novamente' e poderá dispensar o anúncio definitivamente"
                              } arrow>
                                <InfoIcon className={classes.tooltipIcon} />
                              </Tooltip>
                            </Box>
                            
                            {values.requiresAcknowledge && (
                              <Typography variant="caption" style={{ 
                                color: '#f57c00', 
                                fontStyle: 'italic',
                                display: 'block',
                                marginTop: 8
                              }}>
                                💡 No modo confirmação, o usuário decide quando parar de ver o anúncio através das opções "Li e entendi" ou "Ver depois"
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                  {/* Seção Status */}
                  <Grid xs={12} item>
                    <Box>
                      <Box className={classes.sectionHeader}>
                        <CheckCircleIcon className={classes.sectionIcon} />
                        <Typography variant="h6">
                          Status do Anúncio
                        </Typography>
                      </Box>
                        
                        <Box className={classes.switchContainer}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.status}
                                onChange={(event) => setFieldValue("status", event.target.checked)}
                                color="primary"
                              />
                            }
                            label="Anúncio ativo"
                            className={classes.switchLabel}
                          />
                        </Box>
                      </Box>
                    </Grid>
                </Grid>
              </TabPanel>
              </DialogContent>

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  className={classes.cancelButton}
                >
                  {i18n.t("announcements.dialog.buttons.cancel")}
                </Button>
                <div className={classes.btnWrapper}>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.saveButton}
                  >
                    {announcementId
                      ? `${i18n.t("announcements.dialog.buttons.edit")}`
                      : `${i18n.t("announcements.dialog.buttons.add")}`}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

// Função helper para fallback de mídia em desenvolvimento
export const getMediaUrlWithFallback = (mediaPath, fallbackText = "announcement") => {
  if (!mediaPath) return null;
  
  // Se estiver em desenvolvimento, usa uma imagem de placeholder
  if (process.env.NODE_ENV === 'development') {
    // Retorna uma imagem de placeholder temática
    const placeholders = {
      announcement: `https://picsum.photos/400/300?random=1`,
      general: `https://picsum.photos/400/300?random=2`,
      maintenance: `https://picsum.photos/400/300?random=3`,
      update: `https://picsum.photos/400/300?random=4`,
      news: `https://picsum.photos/400/300?random=5`,
    };
    return placeholders[fallbackText] || placeholders.announcement;
  }
  
  // Em produção, usa o caminho real
  return mediaPath;
};

export default AnnouncementModal;
